import {
  type FieldMetadata,
  getInputProps,
  getTextareaProps,
  useField,
  useFormMetadata,
  unstable_useControl as useControl,
} from '@conform-to/react';
import type { FocusEvent, HTMLAttributes } from 'react';
import { useMemo, useRef, useState } from 'react';
import { type CampusConfiguration, selectConfiguredGrades } from '../../util/data/campus-configuration';
import { cn } from '../../util';
import { type FieldProps, Field as InnerField, type SelectFieldProps } from '../Fields/Field';
import { FieldError, type FieldErrorProps } from '../Fields/FieldError';
import { FieldLabel, type FieldLabelProps } from '../Fields/FieldLabel';
import { FieldSelect as InnerFieldSelect } from '../Fields/FieldSelect';
import { FieldTextArea as InnerFieldTextArea } from '../Fields/FieldTextArea';
import { FieldWrapper } from '../Fields/FieldWrapper';
import { FieldRadioCheckbox } from '../Fields/RadioCheckbox/FieldRadioCheckbox';
import type { OptionType } from '../Inputs/Select';
import { RadioGroup } from '../Inputs/radio-group';
import { useFormLevelError } from './Form';

const firstError = (meta: { errors?: string[] }) => meta.errors?.[0];

// Note: checkboxes and radios have their own adapters below
const textInputTypes = new Set([
  'color',
  'date',
  'datetime-local',
  'email',
  'file',
  'hidden',
  'month',
  'number',
  'password',
  'range',
  'search',
  'tel',
  'text',
  'time',
  'url',
  'week',
]);

type TextInputType = Exclude<Parameters<typeof getInputProps>[1]['type'], 'checkbox' | 'radio'>;

export const Field = ({ name, type = 'text', ...props }: FieldProps) => {
  const [meta] = useField<string>(name);
  const inputType = (textInputTypes.has(type) ? type : 'text') as TextInputType;
  const { key, ...inputProps } = getInputProps(meta, { type: inputType });

  return <InnerField key={key} {...props} {...inputProps} type={type} error={firstError(meta)} />;
};

export const FieldTextArea = ({ name, ...props }: FieldProps<HTMLTextAreaElement>) => {
  const [meta] = useField<string>(name);
  const { key, ...textareaProps } = getTextareaProps(meta);

  return <InnerFieldTextArea key={key} {...props} {...textareaProps} error={firstError(meta)} />;
};

export const FieldCheckbox = ({
  name,
  value = 'true',
  ...props
}: Omit<FieldProps<HTMLInputElement>, 'value'> & { value?: string }) => {
  const [meta] = useField<string>(name);
  const { key, ...inputProps } = getInputProps(meta, { type: 'checkbox', value });

  return <FieldRadioCheckbox key={key} {...props} {...inputProps} type="checkbox" error={firstError(meta)} />;
};

export interface FieldSelectProps extends Omit<SelectFieldProps, 'value' | 'defaultValue' | 'onChange'> {
  /** Called with the newly selected value after the form value has been updated. */
  onValueChange?: (value: string) => void;
}

// Note: multi-selects store their values as one comma-separated string, as the 0.9 adapter did. Their defaults
// must be comma-separated strings too: Conform's reset writes an array default into a text input as its first item.
const toControlValue = (value: unknown) =>
  Array.isArray(value) ? value.join(',') : typeof value === 'string' ? value : undefined;

/**
 * Connects a custom widget (react-select, Radix radios) to Conform through a named input we render ourselves.
 * Rendering it (rather than letting Conform add one after hydration) keeps the value in the server-rendered
 * form, and gives Conform a real field to submit, reset, validate, blur and focus. Focusing it (as Conform does
 * for the first invalid field) moves focus to the visible widget.
 */
const useControlledField = (
  name: string,
  getFocusTarget: (input: HTMLInputElement) => HTMLElement | null | undefined,
) => {
  const [meta] = useField<string>(name);
  if (process.env.NODE_ENV !== 'production' && Array.isArray(meta.initialValue)) {
    console.error(`${name}: pass multi-select defaults as a comma-separated string, not an array`);
  }
  const control = useControl<string>({ key: meta.key, initialValue: toControlValue(meta.initialValue) });
  // Note: not `type="hidden"`: React re-applies `defaultValue` on every render, and for hidden inputs that is
  // also the current value. A visually hidden text input keeps the two apart and can receive focus.
  const [defaultValue] = useState(() => toControlValue(meta.initialValue) ?? '');

  const input = (
    <input
      ref={control.register}
      type="text"
      className="sr-only"
      tabIndex={-1}
      aria-hidden
      autoComplete="off"
      name={meta.name}
      form={meta.formId}
      defaultValue={defaultValue}
      onFocus={(event) => {
        // Note: `control.focus()` dispatches synthetic focus events at this input; only forward real focus
        if (document.activeElement === event.currentTarget) getFocusTarget(event.currentTarget)?.focus();
      }}
    />
  );

  return { meta, control, input };
};

/** react-select is not a native form control, so Conform drives it through a control input. */
export const FieldSelect = ({ name, onValueChange, onInputBlur, onInputFocus, ...props }: FieldSelectProps) => {
  // Note: the control input is rendered right before the select's field
  const { meta, control, input } = useControlledField(name, (controlInput) =>
    controlInput.nextElementSibling?.querySelector<HTMLElement>('input[role="combobox"]'),
  );

  return (
    <>
      {input}
      <InnerFieldSelect
        {...props}
        // Note: an empty name keeps react-select's own hidden input out of the submitted FormData
        name=""
        value={toControlValue(control.value) ?? ''}
        error={firstError(meta)}
        onChange={(newValue) => {
          const value = Array.isArray(newValue) ? newValue.join(',') : ((newValue as string | undefined) ?? '');
          control.change(value);
          onValueChange?.(value);
        }}
        // Note: react-select reports focus changes through onFocus/onBlur
        onFocus={(event: FocusEvent<HTMLInputElement>) => {
          onInputFocus?.(event as never);
          control.focus();
        }}
        onBlur={(event: FocusEvent<HTMLInputElement>) => {
          onInputBlur?.(event);
          control.blur();
        }}
      />
    </>
  );
};

interface Radio {
  id?: string;
  label: string;
  labelProps?: FieldLabelProps;
  value: string;
}

export interface FieldRadioGroupProps {
  name: string;
  label?: string;
  labelProps?: FieldLabelProps;
  className?: string;
  type?: string;
  radios: Radio[];
  innerWrapperProps?: Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue' | 'dir'>;
  /** Called with the newly selected value after the form value has been updated. */
  onValueChange?: (value: string) => void;
}

/** Radix radios are buttons rather than native inputs, so Conform drives the group through a control input. */
export const FieldRadioGroup = ({
  name,
  label,
  labelProps,
  className,
  type,
  radios,
  innerWrapperProps,
  onValueChange,
}: FieldRadioGroupProps) => {
  const radioGroupRef = useRef<HTMLDivElement>(null);
  const { meta, control, input } = useControlledField(
    name,
    () =>
      radioGroupRef.current?.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]') ??
      radioGroupRef.current?.querySelector<HTMLElement>('[role="radio"]:not([disabled])'),
  );
  const error = firstError(meta);

  return (
    <FieldWrapper name={name} type={type} error={error} className={cn(className, { hidden: type === 'hidden' })}>
      {input}
      {label && (
        <FieldLabel className="text-sm font-bold" htmlFor={meta.id} {...labelProps}>
          {label}
        </FieldLabel>
      )}

      <RadioGroup
        {...innerWrapperProps}
        ref={radioGroupRef}
        id={meta.id}
        className={cn('radio-group', innerWrapperProps?.className)}
        value={toControlValue(control.value) ?? ''}
        onValueChange={(value) => {
          control.change(value);
          onValueChange?.(value);
        }}
        onFocus={control.focus}
        onBlur={(event) => {
          // Note: moving between radios in the group isn't leaving the field
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) control.blur();
        }}
      >
        {radios.map((radio) => {
          const radioId = radio.id || `${meta.id}-${radio.value}`;

          return (
            <FieldRadioCheckbox
              key={radioId}
              id={radioId}
              // Note: the group owns the value, so the individual Radix items must not be named form controls
              name=""
              type="radio"
              value={radio.value}
              label={radio.label}
              labelProps={{ ...radio.labelProps, htmlFor: radioId }}
              errorComponent={() => null}
            />
          );
        })}
      </RadioGroup>

      <FieldError name={name} error={error} />
    </FieldWrapper>
  );
};

export const GradeSelect = ({
  label = 'Grade',
  name = 'grade',
  campusConfiguration,
  ...props
}: Omit<FieldSelectProps, 'label' | 'name' | 'options'> & {
  label?: string;
  name?: string;
  campusConfiguration?: CampusConfiguration;
}) => {
  const grades = useMemo(() => selectConfiguredGrades(campusConfiguration), [campusConfiguration]) as OptionType[];

  return <FieldSelect placeholder="Click here to select..." name={name} options={grades} label={label} {...props} />;
};

/** Shows the form-level error from Conform's result, falling back to the `error` passed to `Form`. */
export const FormError = (props: FieldErrorProps) => {
  const form = useFormMetadata();
  const formLevelError = useFormLevelError();
  return <FieldError {...props} error={form.errors?.[0] ?? formLevelError ?? undefined} />;
};

export const FormFieldError = ({ name, ...props }: FieldErrorProps & { name: string }) => {
  const [meta] = useField<unknown>(name);
  return <FieldError {...props} name={name} error={firstError(meta)} />;
};
